import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/components/ui/utils';
import { useWeek, type WeekDay } from '../hooks/useWeek';
import { AppTopBar } from './AppTopBar';
import { SectionHeader } from './SectionHeader';
import { SkillCard } from './SkillCard';
import { InternBadge } from './shared/Badge';
import { InternEmptyState, InternErrorState } from './shared/InternEmptyState';
import { DashboardSkeleton } from './shared/InternSkeletons';
import type { ProjectTask } from '../types';

const WEEKDAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function WeekPage() {
  const { user } = useAuth();
  const { userProfile } = usePermissions();
  const { data, loading, error, refresh } = useWeek();

  const userName =
    userProfile?.display_name || user?.email?.split('@')[0] || 'there';

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-warm-white)' }}>
      <AppTopBar userName={userName} />

      <main className="mx-auto max-w-[1100px] px-6 py-10">
        {loading && <DashboardSkeleton />}
        {error && <InternErrorState onRetry={refresh} />}

        {!loading && !error && data && (
          <div className="space-y-10">
            <header>
              <h1
                className="font-serif font-[600] text-[36px] leading-none tracking-[-0.02em]"
                style={{ fontFamily: 'var(--font-serif)', color: 'var(--color-near-black)' }}
              >
                This Week
              </h1>
              <p className="text-[15px] text-[var(--color-charcoal)] mt-2">
                {formatRange(data.weekStart, data.weekEnd)} — what's ahead and what you've finished.
              </p>
            </header>

            {/* Carry-over from earlier */}
            {data.overdue.length > 0 && (
              <section aria-label="Carry-over">
                <div className="rounded-[4px] border border-[rgba(154,41,41,0.3)] bg-[rgba(154,41,41,0.04)] px-4 py-3">
                  <p className="text-[13px] font-[600] text-[var(--destructive)] uppercase tracking-wide mb-3">
                    Carry-over — overdue and still open
                  </p>
                  <div className="space-y-2">
                    {data.overdue.map((task) => (
                      <TaskRow key={task.id} task={task} showDue />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Week grid */}
            <section aria-label="Days this week">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
                {data.days.map((day, i) => (
                  <DayColumn key={day.date} day={day} abbr={WEEKDAY_ABBR[i]} />
                ))}
              </div>
            </section>

            {/* Skills backlog */}
            <section aria-label="Skills to build">
              <SectionHeader title="Skills to build" summary={data.skills.length > 0 ? `${data.skills.length} queued` : undefined} />
              {data.skills.length === 0 ? (
                <InternEmptyState
                  title="No skills queued"
                  body="When you finish your tasks, this is where new skills to explore will show up."
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.skills.map((skill) => (
                    <SkillCard key={skill.id} item={skill} />
                  ))}
                </div>
              )}
            </section>

            <div className="h-16" aria-hidden />
          </div>
        )}
      </main>
    </div>
  );
}

function DayColumn({ day, abbr }: { day: WeekDay; abbr: string }) {
  const dayNum = Number(day.date.split('-')[2]);
  return (
    <div
      className={cn(
        'rounded-[4px] border bg-white p-3 min-h-[140px] flex flex-col',
        day.isPast && 'opacity-75'
      )}
      style={{
        borderColor: day.isToday ? 'var(--color-gold)' : 'rgba(27,58,45,0.12)',
        boxShadow: day.isToday ? '0 0 0 1px var(--color-gold)' : undefined,
      }}
    >
      <div className="flex items-baseline justify-between mb-2">
        <span
          className="text-[12px] font-[600] uppercase tracking-[0.06em]"
          style={{ color: day.isToday ? 'var(--color-gold-dark)' : 'var(--color-mid-gray)' }}
        >
          {abbr}
        </span>
        <span
          className="text-[16px] font-[600]"
          style={{ color: 'var(--color-near-black)' }}
        >
          {dayNum}
        </span>
      </div>

      <div className="flex-1 space-y-1.5">
        {day.tasks.length === 0 ? (
          <p className="text-[12px] text-[var(--color-mid-gray)]">—</p>
        ) : (
          day.tasks.map((task) => <TaskChip key={task.id} task={task} />)
        )}
      </div>

      {day.checklistTotal > 0 && (
        <div className="mt-2 pt-2 border-t border-[var(--border)]">
          <span className="text-[12px] font-[500] text-[var(--color-mid-gray)]">
            Checklist {day.checklistDone}/{day.checklistTotal}
          </span>
        </div>
      )}
    </div>
  );
}

function TaskChip({ task }: { task: ProjectTask }) {
  const navigate = useNavigate();
  const done = task.status === 'done';
  return (
    <button
      type="button"
      onClick={() => navigate(`/intern/task/${task.id}`)}
      className={cn(
        'block w-full text-left rounded-[4px] border px-2 py-1.5 transition-colors duration-150',
        'hover:bg-[var(--muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-gold)]'
      )}
      style={{ borderColor: 'rgba(27,58,45,0.12)' }}
      title={task.title}
    >
      <span
        className={cn(
          'text-[13px] font-[500] line-clamp-2',
          done && 'line-through text-[var(--color-mid-gray)]'
        )}
        style={!done ? { color: 'var(--color-near-black)' } : undefined}
      >
        {task.title}
      </span>
      <span className="mt-1 block">
        <InternBadge variant={taskVariant(task)} dot={false}>{statusLabel(task)}</InternBadge>
      </span>
    </button>
  );
}

function TaskRow({ task, showDue }: { task: ProjectTask; showDue?: boolean }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(`/intern/task/${task.id}`)}
      className="flex w-full items-center justify-between gap-3 rounded-[4px] border border-[var(--border)] bg-white px-4 py-2.5 text-left hover:bg-[var(--muted)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-gold)]"
    >
      <span className="text-[14px] font-[500] text-[var(--color-near-black)] truncate">{task.title}</span>
      <span className="flex items-center gap-3 shrink-0">
        {showDue && task.due_date && (
          <span className="text-[13px] text-[var(--destructive)] font-[500]">due {formatMD(task.due_date)}</span>
        )}
        <InternBadge variant={taskVariant(task)} dot={false}>{statusLabel(task)}</InternBadge>
      </span>
    </button>
  );
}

function taskVariant(task: ProjectTask): 'pending' | 'active' | 'completed' | 'blocked' {
  if (task.status === 'done') return 'completed';
  if (task.status === 'blocked') return 'blocked';
  if (task.status === 'in_progress') return 'active';
  return 'pending';
}

function statusLabel(task: ProjectTask): string {
  return { not_started: 'Not started', in_progress: 'In progress', done: 'Done', blocked: 'Blocked' }[task.status] ?? task.status;
}

function formatMD(date: string): string {
  return new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatRange(start: string, end: string): string {
  return `${formatMD(start)} – ${formatMD(end)}`;
}
